import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { alertsApi, farmApi } from '../lib/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const severityConfig = {
  critical: { color: 'bg-red-100 text-red-800 border-red-200', badge: 'bg-red-500' },
  high: { color: 'bg-orange-100 text-orange-800 border-orange-200', badge: 'bg-orange-500' },
  medium: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', badge: 'bg-yellow-500' },
  low: { color: 'bg-blue-100 text-blue-800 border-blue-200', badge: 'bg-blue-500' },
};

export default function Alerts() {
  const queryClient = useQueryClient();
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [farmFilter, setFarmFilter] = useState<string>('');

  const { data: alertsData, isLoading } = useQuery({
    queryKey: ['alerts', severityFilter, farmFilter],
    queryFn: () => alertsApi.list(farmFilter || undefined, severityFilter || undefined),
  });

  const { data: farms = [] } = useQuery({
    queryKey: ['farms'],
    queryFn: farmApi.list,
  });

  const { data: summary } = useQuery({
    queryKey: ['alerts-summary'],
    queryFn: alertsApi.getSummary,
  });

  const acknowledgeMutation = useMutation({
    mutationFn: alertsApi.acknowledge,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alerts-summary'] });
      toast.success('Alert acknowledged');
    },
  });

  const alerts = alertsData?.alerts || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Alerts</h1>
        <p className="text-gray-500">Monitor and manage farm health alerts</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {(['critical', 'high', 'medium', 'low'] as const).map((severity) => (
          <button
            key={severity}
            onClick={() => setSeverityFilter(severityFilter === severity ? '' : severity)}
            className={`p-4 rounded-xl border-2 text-left transition-all ${
              severityFilter === severity ? 'ring-2 ring-offset-2 ring-green-500' : ''
            } ${severityConfig[severity].color}`}
          >
            <p className="text-sm font-medium capitalize">{severity}</p>
            <p className="text-2xl font-bold mt-1">{summary?.bySeverity?.[severity] || 0}</p>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 bg-white rounded-xl shadow-sm border p-4">
        <FunnelIcon className="h-5 w-5 text-gray-400" />
        <select
          value={farmFilter}
          onChange={(e) => setFarmFilter(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm"
        >
          <option value="">All Farms</option>
          {farms.map((farm) => (
            <option key={farm.id} value={farm.id}>{farm.name}</option>
          ))}
        </select>
        <select
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm"
        >
          <option value="">All Severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        {(severityFilter || farmFilter) && (
          <button
            onClick={() => { setSeverityFilter(''); setFarmFilter(''); }}
            className="text-sm text-green-600 hover:underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Alert List */}
      <div className="bg-white rounded-xl shadow-sm border">
        {alerts.length > 0 ? (
          <div className="divide-y">
            {alerts.map((alert) => {
              const farm = farms.find((f) => f.id === alert.farmId);
              const config = severityConfig[alert.severity as keyof typeof severityConfig];
              
              return (
                <div key={alert.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className={`p-2 rounded-lg ${config.color}`}>
                        <ExclamationTriangleIcon className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs text-white ${config.badge}`}>
                            {alert.severity}
                          </span>
                          <span className="text-xs text-gray-500">
                            {alert.alertType.replace('_', ' ')}
                          </span>
                        </div>
                        <p className="font-medium text-gray-900 mt-1">{alert.message}</p>
                        <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                          {farm && (
                            <Link to={`/farms/${farm.id}`} className="hover:text-green-600">
                              {farm.name}
                            </Link>
                          )}
                          <span>{format(new Date(alert.detectedAt), 'MMM d, h:mm a')}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {alert.acknowledgedAt ? (
                        <span className="flex items-center text-green-600 text-sm">
                          <CheckCircleIcon className="h-5 w-5 mr-1" />
                          Acknowledged
                        </span>
                      ) : (
                        <button
                          onClick={() => acknowledgeMutation.mutate(alert.id)}
                          disabled={acknowledgeMutation.isPending}
                          className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
                        >
                          Acknowledge
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-12 text-center">
            <CheckCircleIcon className="h-16 w-16 mx-auto text-green-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No alerts</h3>
            <p className="text-gray-500">All your farms are healthy!</p>
          </div>
        )}
      </div>
    </div>
  );
}
